import React from "react";
import classNames from "classnames";

import { comnUtils } from "@/comn/utils";
import { FormControlCommonProps } from "../FormControl";
import { COLOR_BORDER, PADDING_R, TEXT_ALIGN } from "@/comn/features/foundation";

export type InputNumberProps = {
    decimalScale?: number;
    thousandSeparator?: boolean;
};

type Props = FormControlCommonProps & InputNumberProps;

export const InputNumber = React.forwardRef<HTMLInputElement, Props>(
    (props: Props, ref: React.ForwardedRef<HTMLInputElement>) => {
        const {
            edit = true,
            mode,
            name,
            value,
            readOnly,
            disabled,
            maxLength,
            placeholder,
            defaultValue,
            align = "right",
            onBlur,
            onFocus,
            onChange,
            onEnter,

            color,
            editColor,
            bold,
            editBold,
            borderColor,
            fontSize,
            rightText,

            decimalScale,
            thousandSeparator = true,
        } = props;

        const _props = Object.fromEntries(
            Object.entries({
                name,
                readOnly,
                disabled,
                maxLength,
                placeholder,
                defaultValue,
                onBlur,
                onFocus,
            }).filter(([, value]) => value !== undefined),
        );
        const o = { decimalScale, thousandSeparator };

        const [_value, _setValue] = React.useState<any>(formatNumber(value, o));

        React.useEffect(() => {
            if (value === _value) return;
            _setValue(formatNumber(value, o));
        }, [value]);

        React.useEffect(() => {
            _setValue(formatNumber(value, o));
        }, [decimalScale, thousandSeparator]);

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            _setValue(formatNumber(e.target.value, o));

            if (!onChange) return;
            onChange(unformatNumber(formatNumber(e.target.value, o), o) ?? null);
        };

        const handleKeyDown = (e: any) => {
            if (e.keyCode === 13) {
                if (onEnter) {
                    onEnter();
                }
            }
        };

        const EDIT_STATUS = mode === "edit" ? true : mode === "view" ? false : edit;

        return (
            <div className="w-full">
                {!EDIT_STATUS && (
                    <div title={_value} className={classNames("w-fit", comnUtils.getViewStyle(color, bold, fontSize))}>
                        {_value}
                    </div>
                )}
                <div hidden={!EDIT_STATUS}>
                    <input
                        {..._props}
                        ref={ref}
                        value={_value}
                        title={_value}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        className={classNames(
                            "input",
                            rightText && PADDING_R[(rightText.length * 3) as keyof typeof PADDING_R],
                            comnUtils.getEditStyle(editColor, editBold),
                            align && TEXT_ALIGN[align],
                            borderColor ? COLOR_BORDER[borderColor] : "",
                        )}
                    />
                </div>
            </div>
        );
    },
);

export const formatNumber = (v: any, o?: any) => {
    if (v === 0 || v === "0") return "0";
    if (v === ".") {
        if (o?.decimalScale === 0) {
            return "";
        }
        return "0.";
    }
    if (!v) return "";

    let f = String(v).replaceAll(",", "");

    if (isNaN(Number(f))) {
        f = f.replaceAll(/\D/g, "");
    }

    if (o?.decimalScale !== undefined) {
        const x = f.split(".");
        const int = x[0];
        const dec = x[1]?.slice(0, o.decimalScale);

        if (o.decimalScale === 0) {
            f = int;
        } else {
            f = int + (dec === undefined ? "" : "." + dec);
        }
    }

    if (o?.thousandSeparator) {
        const x = f.split(".");
        const int = Number(x[0]).toLocaleString();
        const dec = x[1];
        f = int + (dec === undefined ? "" : "." + dec);
    }

    return f;
};

export const unformatNumber = (v: any, o?: any) => {
    if (v === 0 || v === "0") return 0;
    if (!v) return undefined;

    let f = String(v).replaceAll(",", "");

    if (isNaN(Number(f))) {
        f = f.replaceAll(/\D/g, "");
    }

    return Number(f);
};
